import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { generatePath, useNavigate } from 'react-router-dom';

import { AvatarCard } from 'us-react-components/dist/AvatarCard/AvatarCard';
import { availabilityTypeConstant, bookingTypeConstant, jobTypeConstant } from 'us-react-components/dist/constants/Booking';
import { Job } from 'us-web-services/dist/types/Job';
import { DateFormat, formatDateFromString } from 'us-react-components/dist/utils/date';
import { Routes } from '../../Routes';
import { Button, ButtonType } from '../Button';

type Props = {
  jobs: Job[];
};

const OCCASIONAL_AVAILABILITY = 3;

const JobBoard = ({ jobs }: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();

  const handleClick = (jobId?: number): void => {
    if (!jobId) {
      return;
    }

    navigate(generatePath('/job/:id', { id: jobId.toString()! }));
  };

  const handleSeeMore = (): void => {
    navigate(generatePath(Routes.sitter.jobBoard));
  }

  const defaultClasses = `grid-cols-1 grid-rows-${jobs.length > 2 ? '3' : jobs.length}`;
  const smClasses = `sm:grid-cols-2 sm:grid-rows-${jobs.length > 2 ? '2' : '1'}`;

  return (
    <div className='mb-10'>
      <h4 className='mb-2 font-bold text-base'>{t('components.jobBoard.heading')}</h4>
      <div className={`grid ${defaultClasses} ${smClasses} gap-4`}>
        {/* we return nine, but design changes require moving to 2x2 for larger screens */}
        {jobs.slice(0, 4).map((job, index) => {
          const threeByOne = index < 3 ? '' : ' hidden sm:block';
          const twoByTwo = index > 2 ? ' hidden sm:block' : '';
          const bookingVertical = bookingTypeConstant.find(type => type.id === job.booking?.typeId)?.label;
          const availability = job.type === jobTypeConstant.RECURRING ? job.availability?.toString() :
            availabilityTypeConstant.find(type => type.id === OCCASIONAL_AVAILABILITY)?.type;
          const start = formatDateFromString(job.booking?.start ?? '', DateFormat.DATE_WEEKDAY_FORMAT);
          const title = `${bookingVertical}, ${availability}`
          const subtitle = `${t('components.jobBoard.startingAt')} ${start}`;

          return (
            <div
              className={`break-words overflow-hidden${threeByOne}${twoByTwo}`}
              key={job.id}
            >
              <AvatarCard
                key={job.id}
                handleClick={(): void => handleClick(job.id)}
                title={<>{title}<br />{subtitle}</>}
                image={job.booking?.parent?.pictureUrl ?? ''}
                // AvatarCard component has default width classes, so we're overriding them here
                className='w-full xl:w-full min-h-[135px]'
                rightContainerClassName='w-[75%] xl:w-[75%] text-sm leading-4 justify-between'
                imageFallback={job.booking?.parent?.firstName?.charAt(0) ?? ''}
              >
                <span className='line-clamp-2 text-xs pt-1'>{job.booking?.description}</span>
                <Button
                  className='font-bold text-sapphire pt-2 pb-0 text-sm self-start'
                  buttonType={ButtonType.tertiary}
                  onClick={():void => handleClick(job.id)}
                >
                  {t('components.jobBoard.ctaText')}
                </Button>
              </AvatarCard>
            </div>
          );
        })}
      </div>
      <div className='flex justify-center items-center pt-3'>
        <Button
          className='font-bold'
          buttonType={ButtonType.tertiary}
          onClick={handleSeeMore}
        >
          {t('components.jobBoard.seeMore')}
        </Button>
      </div>
    </div>
  );
};

export { JobBoard };
